import {
  createElement,
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';

import { useDesignTokens, defaultDesignTokens } from '../../theme';
import {
  SettingsChangeEventInfo,
  SettingsChangeEvent,
  useSubscribeForSettingsChange,
  fireSettingsChange,
} from './events/SettingsChangeEvent';
import { hsl, parseToHsl } from 'polished';
import { useLanguage } from '../../locale/useTextLocalizer';
import { useApplyGlobalSettingsChange } from './hooks/useApplyGlobalSettingsChange';
import { PreferenceItem } from '@sas/nova-core/services/preferences';
import { useGetSettings } from './hooks/useGetSettings';
import { GlobalSettingsKeys, UI5_NO_COLOR_VALUE } from './GlobalSettingsKeys';
import { useSetSettings } from './hooks/useSetSettings';
import { useDeleteSettings } from './hooks/useDeleteSettings';
import { useListenInvertAppColorChange } from './hooks/useListenInvertAppColorChange';
import { useNCTextLocalizer } from '../../locale/useNCTextLocalizer';
import { useMessageDialogText, MessageDialog } from '../MessageDialog';
import { useAppSwitcherPreferenceItemsMessaging } from './hooks/useAppSwitcherPreferenceItemsMessaging';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useConfig } from '../../hooks/useConfig';

// imports for SettingsServiceInfoContext
import { getGlobalSettingsDataProvider } from './__internal__/GlobalSettingsDataProvider';
import { TransientPreferenceService } from './remoteProxies/TransientPreferenceService';
import { useHasSettingsWriteAccess } from './hooks/useHasSettingsWriteAccess';
import { AppliedThemeType, CollectionType, PreferenceType } from '@sas/nova-core/services';
import fallbackGlobalSettingsDataProvider from './__internal__/FallbackGlobalSettingsDP';

export interface GlobalSettingsDataProvider {
  // is the application theme to be applied in the runtime.
  getTheme: () => Promise<AppliedThemeType>;
  // detects if the logged-in user has write access to mutate the user settings or not.
  hasWriteAccess: () => Promise<boolean>;
  // invocation handle to save the user settings changed by the user.
  setSettings: (data: Array<PreferenceItem>) => Promise<CollectionType<PreferenceType>>;
  // invocation handle to fetch the user settings.
  getSettings: () => Promise<CollectionType<PreferenceType>>;
  // invocation handle to delete the user settings which are reset by the user.
  deleteSettings: (keys: Array<string>) => Promise<void>;
}

function getGlobalSettingsKnownDefaults(): GlobalSettingsState {
  return {
    [GlobalSettingsKeys.THEMES_KEY]: 'sas_corporate',
    [GlobalSettingsKeys.PLAY_EARCONS]: 'false',
    [GlobalSettingsKeys.PLAY_VISUAL_EFFECTS]: 'true',
    [GlobalSettingsKeys.ENABLE_POPUP_DISPLAY_DURATION]: 'false',
    [GlobalSettingsKeys.POPUP_DISPLAY_DURATION]: '5',
    [GlobalSettingsKeys.FOCUS_COLOR]: defaultDesignTokens.stateFocusNova.onLightBg.bd,
    [GlobalSettingsKeys.FOCUS_THICKNESS]: defaultDesignTokens.stateFocusNova.onLightBg.bdWidth,
    [GlobalSettingsKeys.FOCUS_OPACITY]: '100',
    [GlobalSettingsKeys.FOCUS_OVERRIDE]: 'false',
    [GlobalSettingsKeys.INVERT_APP_COLORS]: 'false',
  };
}

// For the User Suppressed Messages, there is a requirement to also mutate the setting from MessageDialog,
// hence have provided an exclusive hook below.

// Offline locale is the only settings Where we don't know what the default value could be
// hence would be returned as undefined.

export function useGetGlobalSetting(
  key: Exclude<
    GlobalSettingsKeys,
    GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES | GlobalSettingsKeys.Avatar
  >
) {
  const service = useGlobalSettingsService();

  const [value, setValue] = useState<string | undefined>(() => service.get(key));

  useEffect(() => {
    function change(value: string | undefined) {
      setValue(value);
    }
    service.registerChangeListener(key, change);
    return () => {
      service.deRegisterChangeListener(key, change);
    };
  }, [service, key]);

  return value;
}

export function useSuppressMessagesGlobalSetting() {
  const service = useGlobalSettingsService();
  const key = GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES;
  const [suppressedMessages, setSuppressedMessages] = useState<string[]>(
    () => service.get(key)?.split('|') || []
  );

  useEffect(() => {
    function change(value: string | undefined) {
      setSuppressedMessages(value?.split('|') || []);
    }
    service.registerChangeListener(key, change);
    return () => {
      service.deRegisterChangeListener(key, change);
    };
  }, [service, key]);

  const updateSuppressedMessages = useCallback(
    (value: string[]) => {
      if (value.length > 0) {
        service.set(GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES, value.join('|'));
      } else {
        service.reset(GlobalSettingsKeys.USER_SUPPRESSED_MESSAGES);
      }
    },
    [service]
  );
  return { suppressedMessages, updateSuppressedMessages };
}

export type GlobalSettingsDispatch = {
  get: (key: GlobalSettingsKeys) => string | undefined;
  set: (key: GlobalSettingsKeys, value: string) => void;
  reset: (key: GlobalSettingsKeys) => void;
  registerChangeListener: (
    key: GlobalSettingsKeys,
    handle: (value: string | undefined) => void
  ) => void;
  deRegisterChangeListener: (
    key: GlobalSettingsKeys,
    handle: (value: string | undefined) => void
  ) => void;
};

const globalSettingsServiceError = () => {
  console.error(
    'The global settings service must be used within GlobalSettingsServiceContext Provider'
  );
};

let globalSettingsService: GlobalSettingsDispatch = {
  get: (key: GlobalSettingsKeys) => getGlobalSettingsKnownDefaults()[key],
  set: () => globalSettingsServiceError(),
  reset: () => globalSettingsServiceError(),
  registerChangeListener: () => globalSettingsServiceError(),
  deRegisterChangeListener: () => globalSettingsServiceError(),
};

// Provided a default value so that any tests involving component/s using
// useGetGlobalSetting hook do not break
const GlobalSettingsDispatchContext = createContext<GlobalSettingsDispatch>(globalSettingsService);

function useGlobalSettingsService() {
  return useContext(GlobalSettingsDispatchContext);
}

let listeners: Record<string, (service: GlobalSettingsDispatch) => void> = {};
let listenerCount = 0;

/**
 * Registers callback that will run when the GlobalSettingsDispatch object changes.
 *
 * Note: `useGetGlobalSetting` hook is the desired way to get settings in a component.
 * This function is only for cases without GlobalSettingsDispatchContext.
 *
 * @param A callback function to for when the GlobalSettingsDispatch object changes.
 * @returns A function that will deregister the provided callback.
 */
export const addOnGlobalSettingsServiceUpdate = (
  listener: (service: GlobalSettingsDispatch) => void
): (() => void) => {
  listener(globalSettingsService);
  const id = listenerCount;
  listeners[id] = listener;
  listenerCount++;
  return () => {
    const { [id]: _, ...rest } = listeners;
    listeners = rest;
  };
};

const onGlobalSettingsServiceUpdate = (service: GlobalSettingsDispatch) => {
  // Object.values was giving a compile error in VSCode.
  Object.keys(listeners).forEach((k) => listeners[k](service));
};

/**
 * Note: `useGetGlobalSetting` hook is the desired way to get settings in a component.
 * This function is only for cases without GlobalSettingsDispatchContext.
 */
export const getGlobalSettingsService = () => globalSettingsService;

export type GlobalSettingsState = Partial<{ [key in GlobalSettingsKeys]: string }>;

function GlobalSettingsApplication(
  props: React.PropsWithChildren<{
    getDefaulApplicationTheme: GlobalSettingsDataProvider['getTheme'];
  }>
) {
  const appId = useConfig()?.get('appId');

  const localizer = useNCTextLocalizer('Settings');

  const { CLOSE } = useMessageDialogText();

  const [itemsToSave, setItemsToSave] = useState<Array<PreferenceItem>>([]);
  const [keysToDelete, setKeysToDelete] = useState<Array<string>>([]);
  // special treatment for only invertAppColor setting mutation using keyboard.
  const [invertAppColorSaveError, setInvertAppColorSaveError] = useState(false);

  // denotes the persisted setting values or their defaults.
  const globalSettingsRef = useRef<GlobalSettingsState>({});

  const changeListenersRef = useRef<Map<string, Array<(value: string | undefined) => void>>>(
    new Map()
  );
  const [persistedGlobalSettings, setPersistedGlobalSettings] = useState<GlobalSettingsState>({});

  // themes, focusColor, focusThickness, formatLocale are a few global settings
  // whose default values needs to be derived.
  const focusToken = useDesignTokens().stateFocusNova.default;
  const focusColor =
    persistedGlobalSettings[GlobalSettingsKeys.FOCUS_COLOR] ?? hsl(parseToHsl(focusToken.bd));
  const focusThickness =
    persistedGlobalSettings[GlobalSettingsKeys.FOCUS_THICKNESS] ??
    focusToken.bdWidth.replace(/px$/, '');

  const defaultLanguage = useLanguage();
  const formatLocale =
    persistedGlobalSettings[GlobalSettingsKeys.FORMAT_LOCALE_KEY] ?? defaultLanguage;

  const [defaultTheme, setDefaultTheme] = useState<string | undefined>(undefined);

  const getDefaultAppTheme = props.getDefaulApplicationTheme;

  const updateDefaultTheme = useCallback(() => {
    getDefaultAppTheme().then(
      (data: { id: string }) => setDefaultTheme(data.id),
      () => {
        console.error('Error fetching default theme from themes service.');
      }
    );
  }, [getDefaultAppTheme]);

  const knownDefaults = getGlobalSettingsKnownDefaults();

  const defaultValues: GlobalSettingsState = {
    ...knownDefaults,
    [GlobalSettingsKeys.THEMES_KEY]: defaultTheme ?? knownDefaults[GlobalSettingsKeys.THEMES_KEY],
    [GlobalSettingsKeys.FORMAT_LOCALE_KEY]: formatLocale,
    [GlobalSettingsKeys.FOCUS_COLOR]: focusColor,
    [GlobalSettingsKeys.FOCUS_THICKNESS]: focusThickness,
  };

  globalSettingsRef.current = {
    ...defaultValues,
    ...persistedGlobalSettings,
  };

  useApplyGlobalSettingsChange({
    ...globalSettingsRef.current,
    [GlobalSettingsKeys.FOCUS_COLOR]: persistedGlobalSettings[GlobalSettingsKeys.FOCUS_COLOR],
    [GlobalSettingsKeys.FOCUS_THICKNESS]:
      persistedGlobalSettings[GlobalSettingsKeys.FOCUS_THICKNESS],
    [GlobalSettingsKeys.FOCUS_OPACITY]: persistedGlobalSettings[GlobalSettingsKeys.FOCUS_OPACITY],
  });

  useInvokeChangeListeners(changeListenersRef.current, globalSettingsRef.current);

  const changeListener = useCallback(
    (_: string, data?: { data?: SettingsChangeEvent }) => {
      const event = data?.data;
      if (event) {
        const globalSettingsIds: string[] = Object.keys(GlobalSettingsKeys).map(
          (key) => GlobalSettingsKeys[key as keyof typeof GlobalSettingsKeys]
        );

        const changes: GlobalSettingsState = {};

        for (const change of event.data) {
          if (globalSettingsIds.indexOf(change.key) >= 0) {
            // UI5 compatibility
            switch (change.key) {
              case GlobalSettingsKeys.THEMES_KEY:
                // eslint-disable-next-line @sas/i18n/jsx-to-localize
                change.value = change.value === 'Default' ? undefined : change.value;

                if (!change.value) {
                  updateDefaultTheme();
                }
                break;

              case GlobalSettingsKeys.FORMAT_LOCALE_KEY:
              case GlobalSettingsKeys.OFFLINE_LOCALE_KEY:
                // eslint-disable-next-line @sas/i18n/jsx-to-localize
                change.value = change.value === 'Default' ? undefined : change.value;
                break;
              case GlobalSettingsKeys.FOCUS_COLOR:
                change.value = change.value === UI5_NO_COLOR_VALUE ? undefined : change.value;
            }

            changes[change.key as GlobalSettingsKeys] = change.value;
          }
        }

        setPersistedGlobalSettings((settings) => {
          const updated: GlobalSettingsState = { ...settings, ...changes };
          return Object.keys(updated).reduce(
            (result: GlobalSettingsState, key) =>
              updated[key as GlobalSettingsKeys] !== undefined
                ? { ...result, [key]: updated[key as GlobalSettingsKeys] }
                : result,
            {}
          );
        });
      }
    },
    [updateDefaultTheme]
  );

  // settings messaging
  useSubscribeForSettingsChange(changeListener);

  // App switcher messaging
  useAppSwitcherPreferenceItemsMessaging(appId);

  const resolveGetSettings = useCallback(
    (items: Array<PreferenceItem>) => {
      const globalSettingsIds: string[] = Object.keys(GlobalSettingsKeys).map(
        (key) => GlobalSettingsKeys[key as keyof typeof GlobalSettingsKeys]
      );

      const globalSettings = items.reduce((globalSettings: { [key: string]: string }, item) => {
        if (globalSettingsIds.indexOf(item.id) >= 0) {
          return { ...globalSettings, [item.id]: item.value };
        }
        return globalSettings;
      }, {});

      if (!globalSettings[GlobalSettingsKeys.THEMES_KEY]) {
        updateDefaultTheme();
      }

      if (globalSettings[GlobalSettingsKeys.FOCUS_COLOR] === UI5_NO_COLOR_VALUE) {
        delete globalSettings[GlobalSettingsKeys.FOCUS_COLOR];
      }

      setPersistedGlobalSettings((settings) => ({ ...settings, ...globalSettings }));
    },
    [updateDefaultTheme]
  );

  const rejectGetSettings = useCallback(() => {
    console.error('Get preferences call to obtain global settings failed.');
  }, []);

  useGetSettings(resolveGetSettings, rejectGetSettings);

  const resolveSetSettings = useCallback(
    (items: Array<PreferenceItem>) => {
      setItemsToSave([]);
      const eventChange = items.reduce((change, item) => {
        return change.concat([
          {
            key: item.id,
            value: item.value || undefined,
            oldValue: globalSettingsRef.current[item.id as GlobalSettingsKeys],
          },
        ]);
      }, [] as SettingsChangeEventInfo);

      fireSettingsChange(eventChange, appId);
    },
    [appId]
  );

  const rejectSetSettings = useCallback(() => {
    setItemsToSave([]);

    const ids = itemsToSave.map((item) => item.id);

    console.error(`Failed to save the following global settings ${ids}`);

    const hasInvertAppColorSetting = ids.indexOf(GlobalSettingsKeys.INVERT_APP_COLORS) >= 0;
    if (hasInvertAppColorSetting) {
      setInvertAppColorSaveError(true);
    }
  }, [itemsToSave]);

  useSetSettings(itemsToSave, resolveSetSettings, rejectSetSettings);

  const resolveDeleteSettings = useCallback(() => {
    setKeysToDelete([]);
    const items: Array<PreferenceItem> = keysToDelete.map((key) => ({ id: key, value: '' }));
    const eventChange = items.reduce((change, item) => {
      return change.concat([
        {
          key: item.id,
          value: item.value || undefined,
          oldValue: globalSettingsRef.current[item.id as GlobalSettingsKeys],
        },
      ]);
    }, [] as SettingsChangeEventInfo);

    fireSettingsChange(eventChange, appId);
  }, [keysToDelete, appId]);

  const rejectDeleteSettings = useCallback(() => {
    console.error(`Failed to save the following global settings ${keysToDelete}`);

    const hasInvertAppColorSetting =
      keysToDelete.indexOf(GlobalSettingsKeys.INVERT_APP_COLORS) >= 0;
    if (hasInvertAppColorSetting) {
      setInvertAppColorSaveError(true);
    }

    setKeysToDelete([]);
  }, [keysToDelete]);

  useDeleteSettings(keysToDelete, resolveDeleteSettings, rejectDeleteSettings);

  const invertAppColorChangeHandler = useCallback(() => {
    const existingValue = Boolean(persistedGlobalSettings[GlobalSettingsKeys.INVERT_APP_COLORS]);
    if (existingValue) {
      setKeysToDelete((keys) => keys.concat(GlobalSettingsKeys.INVERT_APP_COLORS));

      setItemsToSave((items) =>
        items.filter((item) => item.id !== GlobalSettingsKeys.INVERT_APP_COLORS)
      );
    } else {
      setKeysToDelete((keys) => keys.filter((key) => key !== GlobalSettingsKeys.INVERT_APP_COLORS));

      setItemsToSave((items) =>
        items.concat({ id: GlobalSettingsKeys.INVERT_APP_COLORS, value: 'true' })
      );
    }
  }, [persistedGlobalSettings]);

  useListenInvertAppColorChange(invertAppColorChangeHandler);

  const service: GlobalSettingsDispatch = useMemo(() => {
    const result = {
      get: (key: GlobalSettingsKeys) => globalSettingsRef.current[key],

      set: (key: GlobalSettingsKeys, value: string) =>
        setItemsToSave((items) => items.concat({ id: key, value })),

      reset: (key: GlobalSettingsKeys) => setKeysToDelete((keys) => keys.concat(key)),

      registerChangeListener: (
        key: GlobalSettingsKeys,
        handle: (value: string | undefined) => void
      ) => {
        const callbacks = changeListenersRef.current.get(key) || [];
        changeListenersRef.current.set(key, [...callbacks, handle]);
      },

      deRegisterChangeListener: (
        key: GlobalSettingsKeys,
        handle: (value: string | undefined) => void
      ) => {
        const callbacks = changeListenersRef.current.get(key);
        if (Array.isArray(callbacks)) {
          changeListenersRef.current.set(
            key,
            callbacks.filter((callback) => callback !== handle)
          );
        }
      },
    };
    globalSettingsService = result;
    onGlobalSettingsServiceUpdate(result);
    return result;
  }, []);

  return (
    <GlobalSettingsDispatchContext.Provider value={service}>
      <MessageDialog
        type="error"
        isOpen={invertAppColorSaveError}
        title={localizer('settings.invertApplicationColorsError.dialog.title')}
        text={localizer('settings.invertApplicationColorsError.error.msg')}
        dismissAction={{
          text: CLOSE,
          onPress: () => setInvertAppColorSaveError(false),
          isDefault: true,
        }}
      />
      {props.children}
    </GlobalSettingsDispatchContext.Provider>
  );
}

function useInvokeChangeListeners(
  listeners: Map<string, Array<(value: string | undefined) => void>>,
  currentValues: GlobalSettingsState
) {
  const previousValues = usePreviousValue(currentValues);

  useEffect(() => {
    if (previousValues !== undefined) {
      for (const key in GlobalSettingsKeys) {
        const id = GlobalSettingsKeys[key as keyof typeof GlobalSettingsKeys];
        const value = currentValues[id];
        const previousValue = previousValues[id];
        if (value !== previousValue) {
          const aListeners = listeners.get(id);
          aListeners?.forEach((listener) => listener.apply(null, [value]));
        }
      }
    }
  }, [listeners, currentValues, previousValues]);
}

// SettingsServiceInfo Context

export type SettingsServiceInfo = {
  writeAccessState: {
    hasWriteAccess: boolean;
    isWriteAccessDetected: boolean;
  };
  dataProvider: Pick<GlobalSettingsDataProvider, 'getSettings' | 'setSettings' | 'deleteSettings'>;
};

const SettingsServiceContext = createContext<SettingsServiceInfo>({
  writeAccessState: {
    isWriteAccessDetected: true,
    hasWriteAccess: true,
  },
  dataProvider: fallbackGlobalSettingsDataProvider,
});

export function useSettingsService() {
  return useContext(SettingsServiceContext);
}

export function GlobalSettingsProvider(
  props: React.PropsWithChildren<{ dataProvider?: GlobalSettingsDataProvider }>
) {
  const dataProvider = props.dataProvider ?? getGlobalSettingsDataProvider();
  const { isWriteAccessDetectionPending, hasWriteAccess } = useHasSettingsWriteAccess(dataProvider); // this hook is backed by a state

  const _preferenceService: SettingsServiceInfo = useMemo(() => {
    let serviceHandle: SettingsServiceInfo['dataProvider'] = dataProvider;

    if (!isWriteAccessDetectionPending && !hasWriteAccess) {
      serviceHandle = new TransientPreferenceService();
    }
    return {
      writeAccessState: {
        isWriteAccessDetected: !isWriteAccessDetectionPending,
        hasWriteAccess,
      },
      dataProvider: serviceHandle,
    };
  }, [dataProvider, isWriteAccessDetectionPending, hasWriteAccess]);

  return (
    <SettingsServiceContext.Provider value={_preferenceService}>
      <GlobalSettingsApplication getDefaulApplicationTheme={dataProvider?.getTheme}>
        {props.children}
      </GlobalSettingsApplication>
    </SettingsServiceContext.Provider>
  );
}
